import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import {
  GetBorrowerDecisioningForProduct,
  GetLenderDecisioningBatches,
  AddNewDecisioningProgress,
  UpdateDecisioningProgress,
  SubscribeToDecisioningBatchPusherUpdates
} from '@app/app/store/decisioning/decisioning.actions';
import { catchError, tap } from 'rxjs/operators';
import { CreateNewAlert } from '@app/app/store/global-alerts/global-alerts.actions';
import { throwError } from 'rxjs';
import { AttributesMap, DecisioningService } from '../../services/decisioning.service';
import { Injectable } from '@angular/core';
import { Decisioning } from '@app/app/interfaces/decisioning.model';
import { DecisioningBatch } from "@app/app/interfaces/decisioning-batch.model";
import { QualifierValue } from '@app/app/interfaces/qualifier.model';
import { PusherService } from '@app/app/services/pusher.service';
import { keyBy } from 'lodash';
import { patch, updateItem } from '@ngxs/store/operators';
import { AuthState } from '../auth/auth.state';
import { RefreshBusinesses } from '../businesses/businesses-list.actions';

export class DecisioningStateModel {
  attributesMap: AttributesMap;
  decisioning: any;
  decisioningProgress: DecisioningBatch[];
  loading: boolean;
}

@State<DecisioningStateModel>( {
  name: 'decisioning',
  defaults: {
    attributesMap: {},
    decisioning: [],
    decisioningProgress: [],
    loading: false,
  }
} )
@Injectable()
export class DecisioningState {

  @Selector()
  static attributesMap( state: DecisioningStateModel ) {
    return state.attributesMap;
  }

  @Selector()
  static decisioning( state: DecisioningStateModel ) {
    return state.decisioning;
  }

  @Selector()
  static decisioningProgress( state: DecisioningStateModel ) {
    return state.decisioningProgress;
  }

  @Selector()
  static loading( state: DecisioningStateModel ) {
    return state.loading;
  }

  constructor(
    private decisioningService: DecisioningService,
    private store: Store,
    private pusherService: PusherService,
  ) {}

  @Action( GetBorrowerDecisioningForProduct )
  getBorrowerDecisioningForProduct(
    ctx: StateContext<DecisioningStateModel>,
    { borrowerId, loanProductId }: GetBorrowerDecisioningForProduct,
  ) {

    ctx.patchState( {
      loading: true,
    } );

    return this.decisioningService.getBorrowerDecisioningForProduct( borrowerId, loanProductId ).pipe(
      catchError( ( err ) => {
        this.store.dispatch( new CreateNewAlert( {
          level: 'error',
          message: 'Unable to fetch Decisioning'
        } ) );
        ctx.patchState( {
          loading: false,
        } );
        return throwError( err );
      } ),
      tap( response => {
        const attributesMap: AttributesMap = response?.data?.attributes || {};
        const decisioning: Decisioning[] = response?.data?.decisioning.map<Decisioning>( dec => {
          const preapprovalQualifierValues = this.formatDecisioningQualifierValues( dec, attributesMap, true );
          const approvalQualifierValues = this.formatDecisioningQualifierValues( dec, attributesMap, false );
          const preapprovalMatched = preapprovalQualifierValues.filter( qv => qv.status === 'accepted' ).length;
          const preapprovalMatchPercentage = Math.round((preapprovalMatched / preapprovalQualifierValues.length ) * 100);
          const approvalMatched = approvalQualifierValues.filter( qv => qv.status === 'accepted' ).length;
          const approvalMatchPercentage = Math.round((approvalMatched / approvalQualifierValues.length ) * 100);
          return {
            ...dec,
            preapprovalQualifierValues,
            approvalQualifierValues,
            preapprovalMatchPercentage,
            approvalMatchPercentage,
          }
        } );
        ctx.setState(
          patch<DecisioningStateModel>({
            loading: false,
            decisioning,
            attributesMap: response.data.attributes,
          })
        )
      })
    )
  }

  @Action(SubscribeToDecisioningBatchPusherUpdates)
  subscribeToDecisioningBatchPusherUpdates({ }: StateContext<DecisioningStateModel>, { }: SubscribeToDecisioningBatchPusherUpdates) {
    this.pusherSubscribe();
  }

  @Action(GetLenderDecisioningBatches)
  getLenderDecisioningBatches(
    ctx: StateContext<DecisioningStateModel>,
    { lenderId }: GetLenderDecisioningBatches,
  ) {
    return this.decisioningService.getLenderDecisioningBatches( lenderId ).pipe(
      tap( response => {
        ctx.setState(
          patch<DecisioningStateModel>({
            decisioningProgress: response.data.decisioningBatches,
          })
        )
      })
    );
  }

  @Action(AddNewDecisioningProgress)
  addNewDecisioningProgress(ctx: StateContext<DecisioningStateModel>, { decisioningBatch }: AddNewDecisioningProgress) {
    ctx.setState(
      patch<DecisioningStateModel>({
        decisioningProgress: [...ctx.getState().decisioningProgress, decisioningBatch],
      })
    );
  }

  @Action(UpdateDecisioningProgress)
  updateDecisioningProgress(ctx: StateContext<DecisioningStateModel>, { decisioningBatch }: UpdateDecisioningProgress) {
    const batchExistsInState = ctx.getState().decisioningProgress.filter(d => d.id === decisioningBatch.id).length > 0;
    if (batchExistsInState === true){
      //Check if batch is complete
      const completionPercent = Math.trunc(decisioningBatch.total ? ((decisioningBatch.totalSucceeded + decisioningBatch.totalFailed) / decisioningBatch.total) * 100 : 0);
      const complete = (completionPercent == 100 && decisioningBatch.totalFailed <= 0);
      ctx.setState(
        patch<DecisioningStateModel>({
          decisioningProgress: updateItem<DecisioningBatch>(x => x.id === decisioningBatch.id, decisioningBatch),
        })
      );

      if(complete) {
        // Refresh Businesses based on Decisioning being completed
        ctx.dispatch(new RefreshBusinesses());
      }
    } else {
      this.store.dispatch(new AddNewDecisioningProgress(decisioningBatch));
    }
  }

  formatDecisioningQualifierValues( decisioning: Decisioning, attributesMap: AttributesMap, preapproval: boolean ): Array<QualifierValue> {
    const groups = {
      accepted: <Array<QualifierValue>> [],
      rejected: <Array<QualifierValue>> [],
    };

    const mappedDecisioningQualifiers = keyBy(decisioning.product.decisioningQualifiers, 'id');

    decisioning.matchData.resultsByQualifier
      .filter( result => !!attributesMap[result?.field] && (mappedDecisioningQualifiers[result.id].isRequiredForPreapproval === preapproval) )
      .forEach( result => {
        const dq = mappedDecisioningQualifiers[result.id];
        if(result) {
          groups[result.matchValue].push( this.decisioningService.getFormattedQualifierValue( dq, result, attributesMap ) );
        }

      } );

    return [
      ...groups.accepted,
      ...groups.rejected,
    ];
  }

  pusherSubscribe() {
    this.pusherService.subscribe({
      name: `decisioning-batch-${this.store.selectSnapshot(AuthState.user)?.institution?.id}`,
      auth: false,
      handler: (event: any, decisioningBatch: DecisioningBatch) => {
        if(event === 'decisioning-batch-created'){
          this.handlePusherMessage('created', decisioningBatch);
        }
        if(event === 'decisioning-batch-updated'){
          this.handlePusherMessage('updated', decisioningBatch);
        }
        return;
      }
    });
  }

  handlePusherMessage(type: string, decisioningBatch: DecisioningBatch) {
    if (type === 'created'){
      this.store.dispatch(new AddNewDecisioningProgress(decisioningBatch));
    }
    if (type === 'updated'){
      this.store.dispatch(new UpdateDecisioningProgress(decisioningBatch));
    }
  }
}
